import { useQuery } from "@apollo/client";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Maximize from "@mui/icons-material/Maximize";
import Minimize from "@mui/icons-material/Minimize";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { MinimizableDialog } from "components/MinimizableDialog";
import { JobStatus, SdsParsingJobsQuery } from "generated-graphql/graphql";
import { terminalJobStatuses } from "hooks/useLongPollingForJob";
import pluralize from "pluralize";
import { useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { SDS_PARSING_JOBS_STATUS } from "../queries";
import { AnalysisRowStatus, AnalyzingRow, AnalyzingTitleBar } from "./parts";

type SdsParsingJob = SdsParsingJobsQuery["sdsParsingJobs"][number];

function rowStatusFromJobStatus(status: JobStatus): AnalysisRowStatus {
  if (status === JobStatus.Succeeded || status === JobStatus.Promoted) {
    return "done";
  }
  if (terminalJobStatuses.includes(status)) {
    return "failed";
  }
  return "inProgress";
}

/**
 * Progress for a batch of SDS parsing jobs. SDS parsing queues one job per
 * uploaded file — unlike document enrichment, where a single job covers the
 * whole batch — so the batch is polled together and rolled up here.
 */
export function SdsAnalyzingDialog({
  open,
  onClose,
  tenantId,
  jobIds,
  dialogId,
  trayIndex,
  onComplete,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** One job per uploaded safety data sheet. */
  jobIds: string[];
  /** Unique ID for this dialog instance (for minimization) */
  dialogId?: string;
  trayIndex?: number;
  /** Called once, when every job in the batch has reached a terminal status. */
  onComplete?: () => void;
}) {
  const { minimize, expand, close, dialogs } = useMinimizedDialogs();
  const navigate = useNavigate();

  const { data, error, stopPolling } = useQuery(SDS_PARSING_JOBS_STATUS, {
    variables: { tenantId, ids: jobIds },
    pollInterval: 1000,
    fetchPolicy: "network-only",
  });

  const jobs: SdsParsingJob[] = useMemo(() => data?.sdsParsingJobs ?? [], [data?.sdsParsingJobs]);

  const { isRunning, succeededCount, totalCount } = useMemo(() => {
    // No response yet reads as running; the first poll has simply not landed.
    if (!data) {
      return { isRunning: true, succeededCount: 0, totalCount: jobIds.length };
    }
    return {
      isRunning: jobs.some((job) => !terminalJobStatuses.includes(job.status)),
      succeededCount: jobs.filter((job) => rowStatusFromJobStatus(job.status) === "done").length,
      totalCount: jobs.length,
    };
  }, [data, jobs, jobIds.length]);

  const isSucceeded = !isRunning && succeededCount > 0;
  const isFailed = !isRunning && succeededCount === 0;

  const completeNotified = useRef(false);
  useEffect(() => {
    if (completeNotified.current || (isRunning && !error)) {
      return;
    }
    completeNotified.current = true;
    stopPolling();
    onComplete?.();
  }, [error, isRunning, stopPolling, onComplete]);

  const currentDialogId = dialogId ?? `sds-analyzing-${jobIds.join("-")}`;
  const dialogMode = dialogs.find((d) => d.id === currentDialogId)?.mode ?? "centered";
  const isMinimized = dialogMode === "minimizedInTray";

  const handleMinimize = useCallback(() => {
    minimize(currentDialogId);
  }, [currentDialogId, minimize]);

  const handleExpand = useCallback(() => {
    expand(currentDialogId);
  }, [currentDialogId, expand]);

  const handleViewChemicals = useCallback(() => {
    navigate(`/o/${tenantId}/chemicals/catalog/chemicals`);
    close(currentDialogId);
  }, [navigate, tenantId, close, currentDialogId]);

  return (
    <MinimizableDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      mode={dialogMode}
      trayIndex={trayIndex}
      wholeBoxClickable
      onMinimizedClick={handleExpand}
      title={
        totalCount > 0 ? (
          <AnalyzingTitleBar
            noun="Safety Data Sheet"
            count={totalCount}
            progressCount={succeededCount}
            totalCount={totalCount}
            isRunning={isRunning}
            isSucceeded={isSucceeded}
            isFailed={isFailed}
          />
        ) : (
          <Skeleton variant="text" width="50%" />
        )
      }
      hideCloseButton
      titleBarActionElement={
        <IconButton
          onClick={(e) => {
            // The whole minimized paper is clickable, so its handler also fires;
            // stop propagation so we don't expand twice.
            if (isMinimized) {
              e.stopPropagation();
              handleExpand();
              return;
            }
            handleMinimize();
          }}
          disableRipple={isMinimized}
          sx={isMinimized ? { "&:hover": { backgroundColor: "transparent" } } : undefined}
        >
          {isMinimized ? <Maximize /> : <Minimize />}
        </IconButton>
      }
      primaryActionButton={
        isRunning ? (
          <Button onClick={handleMinimize} variant="text" size="medium">
            Minimize
          </Button>
        ) : (
          <Button
            onClick={handleViewChemicals}
            variant="text"
            size="medium"
            endIcon={isSucceeded ? <ChevronRight /> : undefined}
          >
            {isSucceeded ? "View Chemicals" : "Exit"}
          </Button>
        )
      }
      hideCancelButton={isRunning}
      cancelButtonProps={{ text: "Close" }}
    >
      <Stack spacing={2}>
        <Box>
          <LinearProgress
            value={isRunning && totalCount > 0 ? (succeededCount / totalCount) * 100 : 100}
            variant={isRunning && succeededCount === 0 ? "indeterminate" : "determinate"}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {statusMessage({ isRunning, succeededCount, totalCount })}
          </Typography>
        </Box>

        <List dense>
          {jobs.length > 0 ? (
            jobs.map((job) => (
              <AnalyzingRow
                key={job.id}
                label={job.document?.title ?? "Safety data sheet"}
                status={rowStatusFromJobStatus(job.status)}
                error={job.stepError}
              />
            ))
          ) : (
            <ListItem>
              <Skeleton variant="text" width="100%" />
            </ListItem>
          )}
        </List>
      </Stack>
    </MinimizableDialog>
  );
}

function statusMessage({
  isRunning,
  succeededCount,
  totalCount,
}: {
  isRunning: boolean;
  succeededCount: number;
  totalCount: number;
}): string {
  if (isRunning) {
    return "Hang tight, we're reading your safety data sheets and building a chemical from each one. This usually takes about 10-20 seconds per file, though larger files may take longer.";
  }

  const failedCount = totalCount - succeededCount;

  if (succeededCount === 0) {
    return `We couldn't read your ${pluralize("safety data sheet", totalCount)}. Your ${pluralize("upload", totalCount)} ${totalCount === 1 ? "is" : "are"} saved in your documents, so you can try again from there.`;
  }

  const created = `We created ${succeededCount} ${pluralize("chemical", succeededCount)} from your uploads. Each one needs review, so check the details in the chemical catalog before using them.`;

  if (failedCount === 0) {
    return created;
  }

  return `${created} We couldn't read ${failedCount} of your ${pluralize("upload", failedCount)}.`;
}
